// BookingCalendar.tsx
import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer, Views, stringOrDate } from 'react-big-calendar';
import moment from 'moment';
import { Button } from '@mui/material';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useHeader } from '../../contexts/HeaderContext';
import { useBookings } from '../../contexts/BookingContext';
import { useFetch } from '../../utils/FetchTemplate';
import { defaultBooking, IBooking } from '../../interfaces/Booking.interface';
import { EventDetailsDialogNew } from './EventDetailDialogNew';
import { IMember } from '../../interfaces/Member.interface';
import { useMembers } from '../../contexts/MemberContext';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../interfaces/constants';
import { useSetting } from '../../contexts/SettingContext';

const localizer = momentLocalizer(moment);

const BookingCalendar: React.FC = () => {
  const { fetchMembers } = useMembers();
  const { bookings, fetchBookings } = useBookings();
  const [dialogOpen, setAddDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const { setHeader } = useHeader();
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date }>({ start: new Date(), end: new Date()});
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const { postData } = useFetch();
  const { user } = useAuth();
  const { settings } = useSetting();
  const role = settings?.profile?.role;
  
  const DnDCalendar = role === Role.Admin ? withDragAndDrop(Calendar) : Calendar;


  useEffect(() => {
    setHeader("Calendar");
  })

  const handleAddDialogOpen = () => {
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    setSelectedSlot(slotInfo);
    setDuration(getTimeDifferenceInMinutes(slotInfo.start, slotInfo.end));
    setAddDialogOpen(true);
  };

  const handleEventDialogOpen = (event: any) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
  };


  const handleAddBooking = async (newBooking: IBooking, newMember?: IMember) => {
    try {
      await postData<IBooking>('/api/booking/create', newBooking);
      await fetchBookings();
      if (newMember) {
        await postData('/api/member/update', {memberId: newMember.memberId, update: newMember})
        await fetchMembers();
      };
    } catch (error) {
    } finally {
    }
  };
  

  const handleDeleteBooking = async (booking: IBooking) => {
    postData<IBooking>('/api/booking/delete', booking).then(()=>{
      fetchBookings()
    }).catch().finally(() =>{
    })
  };

  //通过recurrence id删除所有recurrence bookings
  const handleDeleteAll = async (recurrenceId: string) => {
    const related = bookings.filter(b => b.recurrenceId === recurrenceId);
    for (const b of related) {
      await handleDeleteBooking(b);
    }
  };

  
  const handleUpdateBooking = async (newBooking: IBooking, newMember?: IMember, closeDialog = true) => {
    try {
      bookings.forEach(booking => {
        if (booking.bookingId === newBooking.bookingId) {
          booking.startTime = newBooking.startTime;
          booking.endTime = newBooking.endTime;
        }
      })
      await postData<IBooking>('/api/booking/update', newBooking);
      await fetchBookings();
      if (newMember) {
        await postData('/api/member/update', {memberId: newMember.memberId, update: newMember})
        await fetchMembers();
      };
    } catch (error) {
    } finally {
    }
  };

  const getTimeDifferenceInMinutes = (date1: Date, date2: Date) => {
    // Convert both dates to milliseconds
    const time1 = new Date(date1).getTime();
    const time2 = new Date(date2).getTime();
  
    // Calculate the difference in milliseconds
    const differenceInMilliseconds = Math.abs(time1 - time2);
  
    // Convert milliseconds to minutes
    const differenceInMinutes = Math.floor(differenceInMilliseconds / (1000 * 60));
  
    return differenceInMinutes;
  }

  const getEndtime = (start: Date, duration: number) => {
    let end = new Date(start);
    end.setMinutes(start.getMinutes() + duration);
    return end;
  }

  const handleEventResize = async ({ event, start, end }: { event: any; start: stringOrDate; end: stringOrDate }) => {
    const updatedBooking: IBooking = {
      ...event,
      startTime: new Date(start),
      endTime: new Date(end),
      duration: getTimeDifferenceInMinutes(new Date(start), new Date(end)),
    };

    await handleUpdateBooking(updatedBooking);
  };

  return (
    <div>
      {(user?.role === Role.Admin) && <Button variant="contained" color="primary" onClick={handleAddDialogOpen}>
        Add Booking
      </Button>}
      <DnDCalendar
        className='mt-2'
        localizer={localizer}
        events={bookings.map(booking => ({
          ...booking,
          startTime: new Date(booking.startTime),
          endTime: new Date(booking.endTime),
        }))}
        startAccessor={(event: any) => event.startTime}
        endAccessor={(event: any) => event.endTime}
        style={{ height: "80vh" }}
        views={[Views.MONTH, Views.WEEK, Views.DAY]}
        defaultView={Views.WEEK}
        date={calendarDate}
        onNavigate={(date) => setCalendarDate(date)}
        min={new Date(2024, 1, 1, 9, 0, 0)}
        max={new Date(2034, 1, 1, 22, 0, 0)}
        selectable
        resizable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleEventDialogOpen}
        onEventResize={handleEventResize}
        onEventDrop={handleEventResize}
        step={5}
        timeslots={12}
      />

      {/* <AddBookingDialog open={dialogOpen} onClose={handleAddDialogClose} onSave={handleAddBooking}
       date={selectedSlot.start} defaultDuration={getTimeDifferenceInMinutes(selectedSlot.start, selectedSlot.end)}/> */}

      {dialogOpen && (user?.role === Role.Admin) && <EventDetailsDialogNew mode="add"
        open={dialogOpen} onClose={handleAddDialogClose} 
        onDelete={handleDeleteBooking} 
        onDeleteAll={handleDeleteAll}
        onUpdate={handleUpdateBooking} 
        onSave={handleAddBooking}
        event={{ ...defaultBooking, startTime: selectedSlot.start, duration: duration, endTime: getEndtime(selectedSlot.start, duration) }}
        includeHistory={false}
        user={user}
      />}

      {selectedEvent && <EventDetailsDialogNew mode="show"
        open={eventDialogOpen} 
        onClose={handleEventDialogClose} 
        onDelete={handleDeleteBooking} 
        onDeleteAll={handleDeleteAll}
        onUpdate={handleUpdateBooking} 
        onSave={handleAddBooking}
        event={selectedEvent} 
        includeHistory={false}
        user={user}
      />}
    </div>
  );
};

export default BookingCalendar;
